import { STANDARDIZED_COLOR_PALETTE } from 'constants/colors';
import { useToast } from 'components/ToastContainer';
import { LayoutStrings } from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';

export const getRandomColor = (): string => {
  const colors = Object.values(STANDARDIZED_COLOR_PALETTE);
  const randomIndex = Math.floor(Math.random() * colors.length); // NOSONAR - Safe for UI color selection
  return colors[randomIndex].background; // Use background color for storage
};

export const generateNewTagId = (): string => {
  return `new_tag_${Date.now()}_${Math.random() // NOSONAR - Safe for UI tag ID generation
    .toString(36)
    .substring(2, 11)}`;
};

export const createHandleSave = (
  saveTags: (
    formValues: any,
    enhancedTags: any
  ) => Promise<{
    success: boolean;
    createSuccesses?: Array<any>;
    updateSuccesses?: Array<any>;
    partialSuccess?: boolean;
    error?: any;
  }>,
  toast: ReturnType<typeof useToast>,
  enhancedTags: any[],
  onTagsUpdated?: () => void,
  onClose?: () => void,
  getTags?: () => void
) => {
  const showSuccessToast = (numCreated?: number) => {
    const message =
      numCreated && numCreated > 1
        ? LayoutStrings.tags.saveSuccessMultiple
        : LayoutStrings.tags.saveSuccessSingle;
    toast.success(message, { timeout: 3000 });
  };

  const showPartialErrorToast = () => {
    toast.error(LayoutStrings.tags.savePartialErrorTitle, {
      content: LayoutStrings.tags.savePartialErrorContent,
      timeout: 5000,
      supportButton: true
    });
  };

  const showErrorToast = () => {
    toast.error(LayoutStrings.tags.saveErrorTitle, {
      content: LayoutStrings.tags.saveErrorContent,
      timeout: 5000,
      supportButton: true
    });
  };

  const handleSuccess = (result: any) => {
    if (result.createSuccesses?.length) {
      showSuccessToast(result.createSuccesses.length);
    }
    // Note: We don't show separate success messages for updates or deletes
    // as the UI feedback is the immediate visual changes in the tag list
    if (onTagsUpdated) onTagsUpdated();
    if (onClose) onClose();
  };

  const handlePartialSuccess = () => {
    showPartialErrorToast();
    if (onTagsUpdated) onTagsUpdated();
    if (getTags) getTags();
  };

  return async (formValues: any) => {
    try {
      const result = await saveTags(formValues, enhancedTags);
      if (result.success) {
        handleSuccess(result);
      } else if (result.partialSuccess) {
        handlePartialSuccess();
      } else {
        showErrorToast();
      }
    } catch {
      showErrorToast();
    }
  };
};

export const createNewTagData = () => {
  const newTagId = generateNewTagId();
  const randomColor = getRandomColor();

  return {
    id: newTagId,
    data: {
      name: '',
      color: randomColor,
      source: 'create'
    }
  };
};

export const shouldEnableSave = (
  loading: boolean,
  saving: boolean,
  values: any,
  enhancedTags: any[],
  validateTagNameWithDuplicates: (
    name: string,
    tagId: string,
    tags: any,
    source?: 'edit' | 'create'
  ) => string | undefined
): boolean => {
  if (loading || saving) {
    return false;
  }

  // Check for valid new tags
  const hasValidNewTags = Object.keys(values.tags || {}).some(tagId => {
    const tagData = values.tags[tagId];
    return (
      tagData.source === 'create' &&
      tagData.name &&
      tagData.name.trim().length > 0
    );
  });

  // Check for changes to existing tags
  const hasChangesToExistingTags = enhancedTags.some(originalTag => {
    const currentTagData = values.tags?.[originalTag.id];

    // If tag doesn't exist in form values, it means it was deleted
    if (!currentTagData) {
      return true;
    }

    // Compare current values with original values
    // Use backgroundColor for comparison since that's what's stored in the form
    const originalBackgroundColor =
      originalTag.backgroundColor || originalTag.color;

    return (
      currentTagData.name !== originalTag.name ||
      currentTagData.color !== originalBackgroundColor
    );
  });

  const shouldEnableSaveButton = hasValidNewTags || hasChangesToExistingTags;

  // Check for validation errors
  const hasValidationErrors = Object.keys(values.tags || {}).some(tagId => {
    const tagData = values.tags[tagId];
    const error = validateTagNameWithDuplicates(
      tagData.name,
      tagId,
      values.tags,
      tagData.source
    );
    return !!error;
  });

  return shouldEnableSaveButton && !hasValidationErrors;
};

export const processDisplayTags = (enhancedTags: any[], values: any) => {
  const existingTags = enhancedTags
    .filter(tag => values.tags?.[tag.id])
    .map(tag => {
      const formData = values.tags[tag.id];
      return {
        ...tag,
        name: formData.name,
        color: formData.color
      };
    });

  const newTagsFromForm = Object.keys(values.tags || {})
    .filter(tagId => !enhancedTags.find(tag => tag.id === tagId))
    .map(tagId => {
      const tagFormData = values.tags[tagId];
      const colorKey =
        Object.keys(STANDARDIZED_COLOR_PALETTE).find(
          key => STANDARDIZED_COLOR_PALETTE[key].primary === tagFormData.color
        ) || 'blue';
      const colorData = STANDARDIZED_COLOR_PALETTE[colorKey];

      return {
        id: tagId,
        name: tagFormData.name,
        color: tagFormData.color,
        backgroundColor: colorData.background,
        textColor: colorData.text,
        colorLabel: colorData.label
      };
    });

  return [...existingTags, ...newTagsFromForm];
};
